import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createDevXGPTClient } from '../api/client';
import type { APIConfig, APIError, Conversation, Message, FileReference } from '../api/types';
import { SerialJobExecutor } from '../util/serial-job-executor';
// import { useDebouncedStream } from './useDebouncedStream';

export interface UseDevXGPTOptions extends APIConfig {
  /**
   * Auto-fetch conversations on mount
   */
  autoFetch?: boolean;
}

export interface UseDevXGPTReturn {
  // State
  conversations: Conversation[];
  messages: Message[];
  currentConversation: Conversation | null;
  isLoading: boolean;
  isSending: boolean;
  error: APIError | null;

  // Actions
  fetchConversations: () => Promise<void>;
  fetchAssistantConversations: (assistantId?: string) => Promise<void>;
  fetchMessages: (conversationId: string) => Promise<void>;
  sendMessage: (message: string, conversationId?: string, assistantId?: string) => Promise<void>;
  startNewConversation: () => void;
  selectConversation: (conversation: Conversation | null) => void;
  clearError: () => void;

  // Streaming state
  streamingMessage: string;
  streamingFiles: FileReference[];
  isStreaming: boolean;

  // Assistant state (current assistant being used)
  currentAssistantId: string | undefined;
  setCurrentAssistantId: (assistantId: string | undefined) => void;
}

/**
 * React Hook for DevXGPT API Integration
 *
 * @example
 * ```tsx
 * const {
 *   conversations,
 *   messages,
 *   fetchConversations,
 *   fetchAssistantConversations,
 *   sendMessage,
 *   isStreaming,
 *   streamingMessage
 * } = useDevXGPT({
 *   authToken: 'your-token',
 *   assistantId: 'asst_123',
 *   autoFetch: true
 * });
 *
 * await fetchConversations();
 * await fetchAssistantConversations('asst_123');
 * ```
 */
export function useDevXGPT(options: UseDevXGPTOptions): UseDevXGPTReturn {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<APIError | null>(null);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [streamingFiles, setStreamingFiles] = useState<FileReference[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentAssistantId, setCurrentAssistantId] = useState<string | undefined>(
    options.assistantId
  );
  // const { addChunk } = useDebouncedStream<string>({
  //   onUpdate: setStreamingMessage,
  //   combiner: (chunks) => chunks.join(''),
  //   delay: 10,
  // });

  // Sync currentAssistantId when assistantId option changes (e.g., user selects different assistant)
  useEffect(() => {
    setCurrentAssistantId(options.assistantId);
  }, [options.assistantId]);

  // Store thread_id for the current conversation
  const threadIdRef = useRef<string | undefined>();

  // AbortController for cancelling requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Serial job executor to prevent race conditions
  const jobExecutor = useRef(new SerialJobExecutor()).current;

  const client = useMemo(() => {
    return createDevXGPTClient({
      authToken: options.authToken,
      useMock: options.useMock,
    });
  }, [options.authToken, options.useMock]);

  /**
   * Fetch all conversations (across all assistants)
   */
  const fetchConversations = useCallback(async () => {
    if (!client) return;

    return jobExecutor.run(async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await client.getConversations();
        setConversations(response.conversations);
      } catch (err) {
        const apiError: APIError = {
          message: err instanceof Error ? err.message : 'Failed to fetch conversations',
          status: (err as any)?.status,
        };
        setError(apiError);
      } finally {
        setIsLoading(false);
      }
    });
  }, [client, jobExecutor]);

  /**
   * Fetch conversations for a specific assistant
   */
  const fetchAssistantConversations = useCallback(
    async (assistantId?: string) => {
      if (!client) return;

      return jobExecutor.run(async () => {
        setIsLoading(true);
        setError(null);

        try {
          const response = await client.getAssistantConversations(assistantId);
          setConversations(response.conversations);
        } catch (err) {
          const apiError: APIError = {
            message: err instanceof Error ? err.message : 'Failed to fetch assistant conversations',
          };
          setError(apiError);
        } finally {
          setIsLoading(false);
        }
      });
    },
    [client, jobExecutor]
  );

  /**
   * Fetch messages for a conversation
   */
  const fetchMessages = useCallback(
    async (conversationId: string, assistantId?: string) => {
      if (!client) return;

      // Use assistantId from argument, or from current state, or from options as fallback
      const effectiveAssistantId = assistantId ?? currentAssistantId ?? options.assistantId;

      console.log('[DevXGPT] fetchMessages called', { conversationId, effectiveAssistantId });

      return jobExecutor.run(async () => {
        setIsLoading(true);
        setError(null);

        try {
          const response = await client.getMessages(conversationId, effectiveAssistantId);
          console.log('[DevXGPT] fetchMessages response', response);
          // Sort messages by created_at ascending (oldest first)
          const sorted = [...response.messages].sort(
            (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
          setMessages(sorted);
          // Persist thread_id from top-level API response for continuing conversation
          if (response.thread_id) {
            threadIdRef.current = response.thread_id;
          } else {
            threadIdRef.current = undefined;
          }
        } catch (err) {
          const apiError: APIError = {
            message: err instanceof Error ? err.message : 'Failed to fetch messages',
          };
          setError(apiError);
        } finally {
          setIsLoading(false);
        }
      });
    },
    [client, currentAssistantId, options.assistantId, jobExecutor]
  );

  /**
   * Send a message with streaming support
   */
  // const sendMessageOld = useCallback(
  //   async (message: string, conversationId?: string, assistantId?: string) => {
  //     if (!client) return;

  //     console.log('[DevXGPT] sendMessage called', { message, conversationId, assistantId });

  //     // Use job executor to prevent race conditions when sending multiple messages quickly
  //     return jobExecutor.run(async () => {
  //       // Cancel any ongoing request
  //       if (abortControllerRef.current) {
  //         abortControllerRef.current.abort();
  //       }

  //       // Create new AbortController for this request
  //       abortControllerRef.current = new AbortController();

  //       setIsSending(true);
  //       setIsStreaming(true);
  //       setStreamingMessage('');
  //       setError(null);

  //       // Add user message immediately
  //       const userMessage: Message = {
  //         id: Date.now(),
  //         conversation_id: conversationId || '',
  //         role: 'user',
  //         message: message,
  //         created_at: new Date().toISOString(),
  //       };
  //       setMessages((prev) => {
  //         const next = [...prev, userMessage];
  //         console.log('[DevXGPT] setMessages after user', next);
  //         return next;
  //       });

  //       try {
  //         await client.sendMessage(
  //           {
  //             message,
  //             conversation_id: conversationId,
  //             assistant_id: assistantId || currentAssistantId,
  //             thread_id: threadIdRef.current,
  //           },
  //           // onChunk
  //           (chunk: string) => {
  //             setStreamingMessage((prev) => {
  //               const next = prev + chunk;
  //               console.log('[DevXGPT] streamingMessage chunk', { chunk, next });
  //               return next;
  //             });
  //           },
  //           // onComplete
  //           (fullMessage: string, newConversationId?: string, newThreadId?: string) => {
  //             console.log('[DevXGPT] streaming complete');
  //             setIsStreaming(false);

  //             // Capture conversation_id and thread_id for future messages
  //             if (newConversationId) {
  //               conversationId = newConversationId;
  //             }
  //             if (newThreadId) {
  //               threadIdRef.current = newThreadId;
  //             }

  //             // Add assistant message
  //             const assistantMessage: Message = {
  //               id: Date.now() + 1,
  //               conversation_id: conversationId || newConversationId || '',
  //               role: 'assistant',
  //               message: fullMessage,
  //               created_at: new Date().toISOString(),
  //             };
  //             setMessages((prev) => {
  //               const next = [...prev, assistantMessage];
  //               console.log('[DevXGPT] setMessages after assistant', next);
  //               return next;
  //             });
  //             setStreamingMessage('');
  //             console.log('[DevXGPT] streamingMessage cleared after complete');

  //             // If this was a new conversation, refresh the conversation list
  //             if (newConversationId && !conversationId) {
  //               fetchConversations();
  //             }
  //           },
  //           // onError
  //           (err: APIError) => {
  //             setError(err);
  //             setIsStreaming(false);
  //             setStreamingMessage('');
  //             console.log('[DevXGPT] sendMessage error', err);
  //           },
  //           // signal
  //           abortControllerRef.current.signal
  //         );
  //       } catch (err) {
  //         // Don't set error if aborted
  //         if (err instanceof Error && err.message !== 'Request aborted') {
  //           const apiError: APIError = {
  //             message: err.message || 'Failed to send message',
  //           };
  //           setError(apiError);
  //           console.log('[DevXGPT] sendMessage catch error', apiError);
  //         }
  //       } finally {
  //         setIsSending(false);
  //         abortControllerRef.current = null;
  //         console.log('[DevXGPT] sendMessage finally');
  //       }
  //     });
  //   },
  //   [client, currentAssistantId, options.assistantId, jobExecutor]
  // );

  /**
   * Send a message with streaming support (new structure)
   */
  const sendMessage = useCallback(
    async (message: string, conversationId?: string, assistantId?: string) => {
      if (!client) return;

      // Use currentConversation if conversationId not provided
      const effectiveConversationId = conversationId || currentConversation?.conversation_id;
      const effectiveThreadId = threadIdRef.current;

      console.log('[DevXGPT] sendMessage called', { 
        message, 
        conversationId: effectiveConversationId, 
        assistantId,
        threadId: effectiveThreadId,
        currentConversation,
      });

      return jobExecutor.run(async () => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        setIsSending(true);
        setIsStreaming(true);
        setStreamingMessage('');
        setStreamingFiles([]);
        setError(null);

        const userMessage: Message = {
          id: Date.now(),
          conversation_id: effectiveConversationId || '',
          role: 'user',
          message: message,
          created_at: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, userMessage]);

        try {
          await client.sendMessage(
            {
              message,
              conversation_id: effectiveConversationId,
              assistant_id: assistantId || currentAssistantId,
              thread_id: effectiveThreadId,
            },
            // onChunk: now receives an object
            (chunk) => {
              // chunk: { text?: string; files?: any[]; id?: number; eventType?: string; annotations?: MessageAnnotation[] }
              if (chunk.text) {
                setStreamingMessage((prev) => {
                  const next = prev + chunk.text;
                  console.log('[DevXGPT] streamingMessage chunk', { chunk, next });
                  return next;
                });
              }
              
              // Handle files
              if (chunk.files && Array.isArray(chunk.files)) {
                setStreamingFiles(chunk.files);
              }
            },
            // onComplete: now receives an object
            (result) => {
              console.log('[DevXGPT] streaming complete', { result });
              setIsStreaming(false);

              const assistantMessage: Message = {
                id: Date.now() + 1,
                conversation_id: result.conversationId || effectiveConversationId || '',
                role: 'assistant',
                message: result.text || '',
                created_at: new Date().toISOString(),
                files: result.files,
              };
              setMessages((prev) => [...prev, assistantMessage]);
              setStreamingMessage('');
              setStreamingFiles([]);
              console.log('[DevXGPT] streamingMessage cleared after complete');

              // Update thread_id and conversation_id refs for future messages
              if (result.threadId) {
                threadIdRef.current = result.threadId;
                console.log('[DevXGPT] Updated threadIdRef', result.threadId);
              }

              if (result.conversationId && !effectiveConversationId) {
                // New conversation created - update currentConversation state so subsequent
                // messages include the conversation_id in the API request
                console.log('[DevXGPT] New conversation created, updating currentConversation', result.conversationId);
                const newConversation: Conversation = {
                  conversation_id: result.conversationId,
                  title: 'New Conversation',
                  assistant_id: assistantId || currentAssistantId || options.assistantId || '',
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                };
                setCurrentConversation(newConversation);
                console.log('[DevXGPT] currentConversation state updated to:', newConversation);
                fetchConversations();
              } else {
                console.log('[DevXGPT] No new conversation ID received. result.conversationId:', result.conversationId, 'effectiveConversationId:', effectiveConversationId);
              }
            },
            // onError
            (err: APIError) => {
              setError(err);
              setIsStreaming(false);
              setStreamingMessage('');
              setStreamingFiles([]);
              console.log('[DevXGPT] sendMessage error', err);
            },
            // signal
            abortControllerRef.current.signal
          );
        } catch (err) {
          if (err instanceof Error && err.message !== 'Request aborted') {
            setError({ message: err.message || 'Failed to send message' });
            console.log('[DevXGPT] sendMessage catch error', err);
          }
        } finally {
          setIsSending(false);
          abortControllerRef.current = null;
          console.log('[DevXGPT] sendMessage finally');
        }
      });
    },
    [client, currentAssistantId, options.assistantId, currentConversation, jobExecutor, fetchConversations]
  );

  /**
   * Start a new conversation
   *
   * Clears the current conversation state. The actual conversation
   * will be created by the backend when sending the first message.
   *
   * @example
   * ```tsx
   * // Start fresh conversation
   * startNewConversation();
   *
   * // Send first message (backend creates conversation automatically)
   * await sendMessage('Hello!');
   * ```
   */
  const startNewConversation = useCallback(() => {
    setCurrentConversation(null);
    setMessages([]);
    threadIdRef.current = undefined;
    setStreamingMessage('');
    setError(null);
  }, []);

  /**
   * Select a conversation
   */
  const selectConversation = useCallback(
    (conversation: Conversation | null) => {
      setCurrentConversation(conversation);
      setCurrentAssistantId(conversation?.assistant_id); // Sync assistant to match conversation
      // Clear messages immediately to show loading skeleton
      setMessages([]);
      // Clear thread_id when switching conversations
      threadIdRef.current = undefined;
      if (conversation) {
        fetchMessages(conversation.conversation_id, conversation.assistant_id);
      }
    },
    [fetchMessages]
  );

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Auto-fetch conversations on mount
  useEffect(() => {
    if (options.autoFetch) {
      fetchConversations();
    }
  }, [options.autoFetch, fetchConversations]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cancel any ongoing requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    // State
    conversations,
    messages,
    currentConversation,
    isLoading,
    isSending,
    error,
    streamingMessage,
    streamingFiles,
    isStreaming,
    currentAssistantId,

    // Actions
    fetchConversations,
    fetchAssistantConversations,
    fetchMessages,
    sendMessage,
    startNewConversation,
    selectConversation,
    clearError,
    setCurrentAssistantId,
  };
}
